import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Switch,
  FormControl,
  FormLabel,
  Select,
  Input,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  Textarea,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiClock, FiSave } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../../utils/init-firebase';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';

// Mirror of functions/CRM/automations/sendWindowConfig.js DEFAULT_SEND_WINDOW.
const DEFAULTS = {
  enabled: true,
  timezoneMode: 'recipient',
  fixedTimezone: 'America/New_York',
  sms: { days: [1, 2, 3, 4, 5, 6], start: '09:00', end: '20:00' },
  email: { days: [1, 2, 3, 4, 5], start: '08:00', end: '18:00' },
  holidays: [],
};

const WEEKDAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (New York)' },
  { value: 'America/Chicago', label: 'Central (Chicago)' },
  { value: 'America/Denver', label: 'Mountain (Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
];

function mergeWithDefaults(cfg) {
  const c = cfg && typeof cfg === 'object' ? cfg : {};
  return {
    enabled: c.enabled !== false,
    timezoneMode: c.timezoneMode === 'fixed' ? 'fixed' : 'recipient',
    fixedTimezone: c.fixedTimezone || DEFAULTS.fixedTimezone,
    sms: { ...DEFAULTS.sms, ...(c.sms || {}) },
    email: { ...DEFAULTS.email, ...(c.email || {}) },
    holidays: Array.isArray(c.holidays) ? c.holidays : [],
  };
}

export default function CampaignSendWindowManager() {
  const toast = useToast();
  const { campaignSendWindow } = useCrmSettings();
  const boxBg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const borderCol = useColorModeValue('gray.200', 'gray.600');

  const [form, setForm] = useState(() => mergeWithDefaults(campaignSendWindow));
  const [saving, setSaving] = useState(false);

  // Resync when settings finish loading / change elsewhere.
  useEffect(() => {
    setForm(mergeWithDefaults(campaignSendWindow));
  }, [JSON.stringify(campaignSendWindow)]);

  const setChannel = useCallback((channel, patch) => {
    setForm((prev) => ({ ...prev, [channel]: { ...prev[channel], ...patch } }));
  }, []);

  const handleSave = async () => {
    // Light client-side validation: each enabled channel needs a valid start < end.
    for (const channel of ['sms', 'email']) {
      const { start, end } = form[channel];
      if (start >= end) {
        toast({
          title: `Invalid ${channel.toUpperCase()} window`,
          description: 'Start time must be before end time.',
          status: 'error',
          duration: 4000,
        });
        return;
      }
    }

    setSaving(true);
    try {
      const holidays = (Array.isArray(form.holidays) ? form.holidays.join('\n') : form.holidays || '')
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));

      const payload = {
        enabled: form.enabled,
        timezoneMode: form.timezoneMode,
        fixedTimezone: form.fixedTimezone,
        sms: { days: [...form.sms.days].sort(), start: form.sms.start, end: form.sms.end },
        email: { days: [...form.email.days].sort(), start: form.email.start, end: form.email.end },
        holidays,
      };

      const functions = getFunctions(app, 'us-east1');
      const updateCrmSettings = httpsCallable(functions, 'updateCrmSettings');
      await updateCrmSettings({ updates: { campaignSendWindow: payload } });

      toast({ title: 'Send window saved', status: 'success', duration: 3000 });
    } catch (err) {
      toast({
        title: 'Could not save send window',
        description: err?.message || 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const renderChannel = (channel, label, helper) => {
    const win = form[channel];
    return (
      <Box borderWidth="1px" borderColor={borderCol} borderRadius="lg" p={4} bg={subtleBg}>
        <Text fontWeight="semibold" mb={1}>{label}</Text>
        <Text fontSize="xs" color="gray.500" mb={3}>{helper}</Text>
        <FormControl mb={3}>
          <FormLabel fontSize="sm" mb={1}>Days</FormLabel>
          <CheckboxGroup
            value={win.days.map(String)}
            onChange={(vals) => setChannel(channel, { days: vals.map((v) => parseInt(v, 10)) })}
          >
            <Wrap spacing={3}>
              {WEEKDAYS.map((d) => (
                <WrapItem key={d.value}>
                  <Checkbox value={String(d.value)}>{d.label}</Checkbox>
                </WrapItem>
              ))}
            </Wrap>
          </CheckboxGroup>
        </FormControl>
        <HStack spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>Earliest</FormLabel>
            <Input
              type="time"
              value={win.start}
              onChange={(e) => setChannel(channel, { start: e.target.value })}
              maxW="160px"
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>Latest</FormLabel>
            <Input
              type="time"
              value={win.end}
              onChange={(e) => setChannel(channel, { end: e.target.value })}
              maxW="160px"
            />
          </FormControl>
        </HStack>
      </Box>
    );
  };

  return (
    <Box bg={boxBg} borderRadius="xl" p={6} shadow="sm">
      <VStack align="stretch" spacing={5}>
        <HStack justify="space-between" align="flex-start">
          <HStack align="center" spacing={3}>
            <FiClock size={22} />
            <Box>
              <Heading size="md">Campaign Send Window</Heading>
              <Text fontSize="sm" color="gray.600">
                Restrict the hours that campaign emails &amp; texts go out. Out-of-window
                messages are held and sent at the next allowed time (never dropped).
              </Text>
            </Box>
          </HStack>
          <Badge colorScheme={form.enabled ? 'green' : 'gray'} fontSize="0.8em" px={2} py={1} borderRadius="md">
            {form.enabled ? 'Active' : 'Off'}
          </Badge>
        </HStack>

        <FormControl display="flex" alignItems="center">
          <Switch
            id="send-window-enabled"
            isChecked={form.enabled}
            onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
            mr={3}
          />
          <FormLabel htmlFor="send-window-enabled" mb={0}>
            Enforce send window for all campaigns
          </FormLabel>
        </FormControl>

        {!form.enabled && (
          <Alert status="warning" borderRadius="md" fontSize="sm">
            <AlertIcon />
            Send window is off — campaigns will send at any hour, including overnight.
          </Alert>
        )}

        <Divider />

        <FormControl>
          <FormLabel fontSize="sm">Timezone basis</FormLabel>
          <Select
            value={form.timezoneMode}
            onChange={(e) => setForm((prev) => ({ ...prev, timezoneMode: e.target.value }))}
            maxW="420px"
          >
            <option value="recipient">Recipient's local time (from property/mailing state)</option>
            <option value="fixed">A single company timezone</option>
          </Select>
          <Text fontSize="xs" color="gray.500" mt={1}>
            Recipient-local is the TCPA-correct basis for texts. When a recipient's
            state is unknown, the fallback timezone below is used.
          </Text>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">
            {form.timezoneMode === 'fixed' ? 'Company timezone' : 'Fallback timezone'}
          </FormLabel>
          <Select
            value={form.fixedTimezone}
            onChange={(e) => setForm((prev) => ({ ...prev, fixedTimezone: e.target.value }))}
            maxW="420px"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </Select>
        </FormControl>

        <Divider />

        {renderChannel('sms', 'Text messages (SMS)', 'Keep within 8:00am–9:00pm local to stay inside TCPA quiet-hour rules.')}
        {renderChannel('email', 'Emails', 'Business hours are recommended for professionalism and deliverability.')}

        <FormControl>
          <FormLabel fontSize="sm">Holidays to skip (optional)</FormLabel>
          <Textarea
            value={Array.isArray(form.holidays) ? form.holidays.join('\n') : form.holidays}
            onChange={(e) => setForm((prev) => ({ ...prev, holidays: e.target.value }))}
            placeholder={'2026-07-04\n2026-12-25'}
            fontFamily="mono"
            fontSize="sm"
            rows={3}
            maxW="420px"
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            One date per line, format YYYY-MM-DD. Sends are pushed to the next open day.
          </Text>
        </FormControl>

        <HStack justify="flex-end">
          <Button
            leftIcon={<FiSave />}
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving"
          >
            Save send window
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
